import { type ChangeEvent, useState, useEffect } from 'react';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotificationStore } from '../../../store/storeNotifications';

import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowParams,
} from '@mui/x-data-grid';
import { Trash } from '@phosphor-icons/react';
import { DimensionSettingsAddDimensionForm } from './DimensionSettingsAddDimensionForm';

import {
  type VectorUpdateDimensionsRequest,
  type VectorUpdateRequest,
  VectorService,
} from '../../../service/VectorService';
import { spacesQueries } from '../../../queries/spaces';
import { vectorQueries } from '../../../queries/vector';

import type { NamespaceDimension } from '../../../types/namespace-dimension';

function DimensionSettings() {
  const queryClient = useQueryClient();

  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    name,
    namespaceId,
    spaceId,
    embeddingModel,

    namespaceDimensions,
    onNamespaceDimensionsChange,
    onNamespaceDimensionsDelete,

    searchUsingDimensions,
    onSearchUsingDimensionsChange,

    isEditing,
  } = useVectorModal();

  const handleSearchUsingDimensionsChange = (
    evt: ChangeEvent<HTMLInputElement>,
  ) => {
    if (isEditing) {
      updateNamespaceFn({
        body: { searchUsingDimensions: evt.target.checked },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      onSearchUsingDimensionsChange(evt.target.value === 'true');

      return;
    }

    onSearchUsingDimensionsChange(evt.target.value === 'true');
  };

  const handleAssignAsDefaultChange = ({
    id,
    isDefault,
    title,
  }: NamespaceDimension) => {
    if (isEditing) {
      updateDimensionsFn({
        body: {
          dimensions: [
            {
              id,
              isDefault: !isDefault,
              title,
            },
          ],
          filesIds: [],
          embeddingModelName: embeddingModel,
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      return;
    }

    // const newDimensionList = test.map(dimension => {
    const newDimensionList = namespaceDimensions.map(dimension => {
      if (dimension.id === id) {
        return {
          ...dimension,
          isDefault: !dimension.isDefault,
        };
      }

      return dimension;
    });

    // setTest(newDimensionList);
    onNamespaceDimensionsChange(newDimensionList);
  };

  const { mutate: updateNamespaceFn } = useMutation({
    mutationFn: ({ body, params }: VectorUpdateRequest) =>
      VectorService.updateNamespace({ body, params }),
    onSuccess: () => {
      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutate: updateDimensionsFn } = useMutation({
    mutationFn: ({ body, params }: VectorUpdateDimensionsRequest) =>
      VectorService.updateDimensions({ body, params }),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Updating dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Failed to update dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onSuccess: (_, variables) => {
      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      setNotification({
        type: 'success',
        message: `Successfully updated dimension ${variables.body.dimensions[0].title}`,
      });

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const columns: Array<GridColDef> = [
    {
      field: 'title',
      headerName: 'Dimension Name',
      editable: false,
      width: 300,
      renderCell: (param: GridRenderCellParams<any, string>) => {
        return (
          <Box
            component="span"
            sx={{
              width: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {param.value}
          </Box>
        );
      },
    },
    {
      field: 'description',
      editable: false,
      headerName: 'Dimension Description',
      width: 300,
      renderCell: (param: GridRenderCellParams<any, Array<string>>) => (
        <span>{param.value}</span>
      ),
    },
    {
      field: 'isDefault',
      type: 'actions',
      width: 200,
      renderHeader: () => (
        <span style={{ fontWeight: 500 }}>Apply to new files</span>
      ),
      getActions: (params: GridRowParams) => [
        <Switch
          checked={params.row.isDefault}
          onChange={() => handleAssignAsDefaultChange(params.row)}
        />,
      ],
    },
    {
      field: 'delete',
      type: 'actions',
      width: 38,
      getActions: (params: GridRowParams) => [
        <Tooltip
          key="dimension-setting-tooltip"
          title="This will permanently delete this dimension from all the chosen files"
          placement="top"
        >
          <GridActionsCellItem
            sx={{
              padding: '0.5rem',
              borderRadius: 0,
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'transparent',

              transition: 'all 150ms ease-in',

              '&:hover': {
                backgroundColor: '#ffebee',
                color: 'red',
                borderColor: 'red',
              },

              '&>svg': {
                transition: 'all 150ms ease-in',
              },
            }}
            icon={<Trash size={16} />}
            label="Delete Dimension"
            onClick={() =>
              onNamespaceDimensionsDelete(params.row.id, params.row.title)
            }
          />
        </Tooltip>,
      ],
    },
  ];

  return (
    <Box
      sx={{
        width: '900px',
        marginInline: 'auto',
        paddingBlockStart: '1.5rem',

        '&>*+*': {
          marginBlockStart: '1.25rem',
        },
      }}
    >
      <Box>
        <Stack direction="row" alignItems="center" rowGap="0.5rem">
          <Typography variant="h3">Manage meta dimensions</Typography>

          <Switch
            checked={searchUsingDimensions}
            onChange={handleSearchUsingDimensionsChange}
          />
        </Stack>

        <Typography>
          Here are the available meta dimensions. You can switch one on to make
          it the default, or turn it off.
          {/*
            Here are the available meta dimensions. You can switch one on to make
            it the default, or turn it off to prevent new files from being
            assigned to it.
            */}
        </Typography>
      </Box>

      {/*<Box>
        <FormControl>
          <FormLabel
            id="namespace-dimension"
            sx={{
              fontWeight: 600,
              color: 'black',
              '&:focus-within': { color: 'black' },
              '&.Mui-focused': { color: 'black' },
            }}
          >
            Turn off dimensions for new files
          </FormLabel>

          <RadioGroup
            aria-labelledby="namespace-dimension"
            defaultValue=""
            name="radio-buttons-group"
            value={`${tempSearchUsingDimensions}`}
            onChange={handleTempSearchUsingDimensionsChange}
          >
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Dimensions will be turned ON for every new file uploaded in this VM"
              sx={{ color: 'black' }}
            />
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Dimensions will be turned OFF for every new file uploaded in this VM"
              sx={{ color: 'black' }}
            />
          </RadioGroup>
        </FormControl>
        </Box>*/}

      <Paper
        sx={{
          minWidth: 'auto !important',
          maxWidth: 'auto !important',
          height: 'auto !important',

          boxShadow: 'none',
        }}
      >
        <DataGrid
          rows={namespaceDimensions}
          // rows={test}
          columns={columns}
          getRowId={row => row.id}
          slotProps={{
            cell: {
              style: {
                textTransform: 'capitalize',
              },
            },
          }}
          onProcessRowUpdateError={error => {
            console.error(error);
          }}
        />
      </Paper>

      <DimensionSettingsAddDimensionForm />
    </Box>
  );
}

export { DimensionSettings };
